import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, BookOpen, MessageSquare, Palette, ArrowRight, Heart } from 'lucide-react';
import FeelingsNeedsHelper from '../FeelingsNeedsHelper';
import EbookPageFooter from '../shared/EbookPageFooter';

export default function OFNRStatementsQuestions() {
  const navigate = useNavigate();
  const [colorScheme, setColorScheme] = useState('standard');
  const [isHelperOpen, setIsHelperOpen] = useState(false);

  const colors = colorScheme === 'standard' 
    ? {
        observation: { bg: 'bg-blue-600', border: 'border-blue-700/30', bgLight: 'bg-blue-900/30', text: 'text-blue-200' },
        feeling: { bg: 'bg-red-600', border: 'border-red-700/30', bgLight: 'bg-red-900/30', text: 'text-red-200' },
        need: { bg: 'bg-green-600', border: 'border-green-700/30', bgLight: 'bg-green-900/30', text: 'text-green-200' },
        request: { bg: 'bg-amber-500', border: 'border-amber-700/30', bgLight: 'bg-amber-900/30', text: 'text-amber-200' }
      }
    : {
        observation: { bg: 'bg-amber-500', border: 'border-amber-700/30', bgLight: 'bg-amber-900/30', text: 'text-amber-200' },
        feeling: { bg: 'bg-red-600', border: 'border-red-700/30', bgLight: 'bg-red-900/30', text: 'text-red-200' },
        need: { bg: 'bg-blue-600', border: 'border-blue-700/30', bgLight: 'bg-blue-900/30', text: 'text-blue-200' },
        request: { bg: 'bg-green-600', border: 'border-green-700/30', bgLight: 'bg-green-900/30', text: 'text-green-200' }
      };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/belonging-ebook')} variant="outline" size="sm" className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800">
                <ArrowLeft className="h-4 w-4 mr-2" />Back to Guide
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">NVC: Statements and Questions (OFNR)</h1>
                <p className="text-slate-300 text-sm">Communicating in the NVC Dance of Honesty and Empathy</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={() => setColorScheme(colorScheme === 'standard' ? 'steve' : 'standard')} variant="outline" size="sm" className="border-purple-600 hover:border-purple-500 text-white hover:bg-purple-900/20">
                <Palette className="h-4 w-4 mr-2" />
                {colorScheme === 'standard' ? "Steve's Colors" : 'Standard Colors'}
              </Button>
              <Button 
                onClick={() => setIsHelperOpen(true)} 
                variant="outline" 
                size="sm" 
                className="border-rose-600 hover:border-rose-500 text-white hover:bg-rose-900/20"
              >
                <Heart className="h-4 w-4 mr-2" />
                Feelings & Needs
              </Button>
              <div className="flex items-center gap-2 text-slate-400 text-sm"><BookOpen className="h-4 w-4" /><span>Page 17</span></div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <MessageSquare className="h-20 w-20 text-purple-400 mx-auto mb-6" />
            <h2 className="text-4xl font-bold text-white mb-4">The NVC Dance of Honesty and Empathy</h2>
            <p className="text-xl text-slate-300 leading-relaxed max-w-4xl mx-auto">
              NVC communication flows between expressing ourselves honestly and listening empathically to others. 
              Both use the same four components: Observation, Feeling, Need, and Request.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            <div className="bg-gradient-to-br from-purple-900/20 to-pink-900/20 border border-purple-700/30 rounded-lg overflow-hidden">
              <div className="bg-purple-900/40 px-6 py-4 border-b border-purple-700/30">
                <h3 className="text-2xl font-bold text-purple-100">Expressing Honestly</h3>
                <p className="text-purple-200 text-sm mt-1">Through statements without blaming or criticizing</p>
              </div>
              <div className="p-6 space-y-6">
                <div className={`${colors.observation.bgLight} border ${colors.observation.border} rounded-lg p-5`}>
                  <h4 className={`text-lg font-bold ${colors.observation.text} mb-2`}>Observation</h4>
                  <p className="text-slate-300 text-sm mb-3 italic">(free from my evaluation)</p>
                  <p className="text-white font-semibold mb-2">"When I observe (see, hear, remember, imagine) …"</p>
                </div>
                <div className={`${colors.feeling.bgLight} border ${colors.feeling.border} rounded-lg p-5`}>
                  <h4 className={`text-lg font-bold ${colors.feeling.text} mb-2`}>Feeling</h4>
                  <p className="text-white font-semibold mb-2">"I feel…"</p>
                </div>
                <div className={`${colors.need.bgLight} border ${colors.need.border} rounded-lg p-5`}>
                  <h4 className={`text-lg font-bold ${colors.need.text} mb-2`}>Need</h4>
                  <p className="text-white font-semibold mb-2">"Because I need/value…"</p>
                </div>
                <div className={`${colors.request.bgLight} border ${colors.request.border} rounded-lg p-5`}>
                  <h4 className={`text-lg font-bold ${colors.request.text} mb-2`}>Request</h4>
                  <p className="text-white font-semibold mb-2">"Would you be willing to…"</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-cyan-900/20 to-blue-900/20 border border-cyan-700/30 rounded-lg overflow-hidden">
              <div className="bg-cyan-900/40 px-6 py-4 border-b border-cyan-700/30">
                <h3 className="text-2xl font-bold text-cyan-100">Listening Empathically</h3>
                <p className="text-cyan-200 text-sm mt-1">Through asking, and receiving without hearing blame or criticism</p>
              </div>
              <div className="p-6 space-y-6">
                <div className={`${colors.observation.bgLight} border ${colors.observation.border} rounded-lg p-5`}>
                  <h4 className={`text-lg font-bold ${colors.observation.text} mb-2`}>Observation</h4>
                  <p className="text-slate-300 text-sm mb-3 italic">(free from your evaluation)</p>
                  <p className="text-white font-semibold mb-2">"When you observe (see, hear, remember, imagine)"</p>
                </div>
                <div className={`${colors.feeling.bgLight} border ${colors.feeling.border} rounded-lg p-5`}>
                  <h4 className={`text-lg font-bold ${colors.feeling.text} mb-2`}>Feeling</h4>
                  <p className="text-white font-semibold mb-2">"Do you feel…?"</p>
                </div>
                <div className={`${colors.need.bgLight} border ${colors.need.border} rounded-lg p-5`}>
                  <h4 className={`text-lg font-bold ${colors.need.text} mb-2`}>Need</h4>
                  <p className="text-white font-semibold mb-2">"Because you need/value…"</p>
                </div>
                <div className={`${colors.request.bgLight} border ${colors.request.border} rounded-lg p-5`}>
                  <h4 className={`text-lg font-bold ${colors.request.text} mb-2`}>Request</h4>
                  <p className="text-white font-semibold mb-2">"Would you like…?"</p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center text-slate-400 text-sm mb-8">
            <p>Steve Torma • The REAL Center • www.theREALcenter.org • 828-254-5613</p>
          </div>

          <div className="flex justify-center mb-8">
            <Button 
              onClick={() => navigate('/workbook/ofnr-practice-sheets')}
              className="bg-green-600 hover:bg-green-500 text-white text-lg px-8 py-4"
            >
              Continue to Practice Sheets (Page 18) <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </div>

          {/* Practice Scenario & Contact Form */}
          <EbookPageFooter 
            pageContext="OFNR Statements & Questions"
            pageNumber={17}
            showScenario={true}
            showContactForm={true}
          />
        </div>
      </div>

      {/* Feelings & Needs Helper Popup */}
      <FeelingsNeedsHelper 
        isOpen={isHelperOpen} 
        onClose={() => setIsHelperOpen(false)} 
      />
    </div>
  );
}
